import { Icon } from '@design/components/icon/Icon';
import { type AppTableFeatures } from '@design/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { DateCell } from '@terminal/widgets/earnings/components/date-cell/DateCell';
import { NumberCell } from '@terminal/widgets/earnings/components/number-cell/NumberCell';
import { handlePriceData } from '@terminal/widgets/earnings/utils/tableUtils';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { t } from 'i18next';

import styles from './EarningsReportingSummary.module.scss';
import { GuidanceCell } from './GuidanceCell';
import { TopicsCell } from './TopicsCell';
import type { EarningsTopicClickHandler, ReportedTodayRow } from './types';

export const SCHEDULED_DATE_COLUMN_ID = 'scheduled-date';

interface GetReportedEarningsColumnsOptions {
  showScheduledDateSortIndicator?: boolean;
  onTopicClick: EarningsTopicClickHandler;
}

export const getReportedEarningsColumns = <TData extends ReportedTodayRow>({
  showScheduledDateSortIndicator = false,
  onTopicClick,
}: GetReportedEarningsColumnsOptions): ColumnDef<AppTableFeatures, TData>[] => [
  {
    id: 'rank',
    header: () => t('widget:earnings.rank'),
    accessorFn: row => row.rank,
    cell: ({ row }) => <TopRankIcon rank={row.original.rank} />,
    sortUndefined: 'last',
  },
  {
    id: 'company',
    header: () => t('widget:earnings.company'),
    accessorFn: row => row.entity_tag,
    cell: ({ row }) => <EntityCell entityTag={row.original.entity_tag} />,
  },
  {
    id: SCHEDULED_DATE_COLUMN_ID,
    header: () =>
      showScheduledDateSortIndicator ? (
        <span className={styles.scheduledDateHeader}>
          {t('widget:earnings.scheduledDate')}
          <Icon
            iconName="SortAscLeft"
            size={12}
            fillColor="var(--icon-primary)"
            data-testid="scheduled-date-sort-icon"
          />
        </span>
      ) : (
        t('widget:earnings.scheduledDate')
      ),
    accessorFn: row => row.reporting_date,
    cell: ({ row }) => (
      <DateCell date={new Date(row.original.reporting_date)} />
    ),
  },
  {
    id: 'guidance',
    header: () =>
      t('widget:themeOverview.tabs.earnings.reportingSummary.guidance'),
    accessorFn: row => row.guidance,
    cell: ({ row }) => <GuidanceCell guidance={row.original.guidance} />,
    sortUndefined: 'last',
  },
  {
    id: 'eps-surprise-pct',
    header: () =>
      t('widget:themeOverview.tabs.earnings.reportingSummary.epsSurprisePct'),
    accessorFn: row => row.eps.surprise_percentage,
    cell: ({ row }) => (
      <NumberCell value={row.original.eps.surprise_percentage} percentage />
    ),
    sortUndefined: 'last',
  },
  {
    id: 'revenue-surprise-pct',
    header: () =>
      t(
        'widget:themeOverview.tabs.earnings.reportingSummary.revenueSurprisePct'
      ),
    accessorFn: row => row.revenue.surprise_percentage,
    cell: ({ row }) => (
      <NumberCell value={row.original.revenue.surprise_percentage} percentage />
    ),
    sortUndefined: 'last',
  },
  {
    id: 'return-1d-pct',
    header: () =>
      t('widget:themeOverview.tabs.earnings.reportingSummary.return1DPct'),
    accessorFn: row => handlePriceData(row.price, 'd1'),
    cell: ({ row }) => (
      <NumberCell
        value={handlePriceData(row.original.price, 'd1')}
        percentage
      />
    ),
    sortUndefined: 'last',
  },
  {
    id: 'earnings-drivers',
    header: () =>
      t('widget:themeOverview.tabs.earnings.reportingSummary.earningsDrivers'),
    cell: ({ row }) => (
      <TopicsCell
        topics={row.original.earningsDrivers ?? []}
        onTopicClick={onTopicClick}
      />
    ),
    enableSorting: false,
  },
];
